import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import SuccessionCfaChoice from './SuccessionCfaChoice';
import SuccessionCfaDisclaimer from './SuccessionCfaDisclaimer';
import SuccessionCfaPrice from './SuccessionCfaPrice';
import SuccessionCfaViewExamples from './SuccessionCfaViewExamples';

// Generating Deal Structures Interstitial - shown while processing
const CfaGeneratingInterstitial = ({ onComplete }) => {
  const intl = useIntl();
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete?.();
    }, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-succession-bg-primary">
      <div className="text-center space-y-8 max-w-2xl px-8">
        <style>{`@keyframes fb-spin{to{transform:rotate(360deg)}}`}</style>
        <div className="w-24 h-24 bg-succession-primary rounded-full flex items-center justify-center mx-auto">
          <div className="w-12 h-12" style={{ animation: 'fb-spin 1s linear infinite' }} aria-hidden="true">
            <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z" />
            </svg>
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.cash_flow_analysis.generating_interstitial.heading' })}</h1>
        <p className="text-xl text-succession-text-secondary">{intl.formatMessage({ id: 'succession.cash_flow_analysis.generating_interstitial.subheading' })}</p>
        <div className="flex justify-center space-x-2">
          <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-3 h-3 bg-succession-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
};

const SuccessionCfaStart = ({ currentUser }) => {
  const [cfaType, updateCfaType] = useState();
  const [step, updateStep] = useState(0);
  const [cashFlows, updateCashFlows] = useState([]);
  const topRef = useRef(null);

  // Scroll to top when component mounts or step changes
  useEffect(() => {
    // Use multiple approaches to ensure scroll works across different scroll containers
    const timer = setTimeout(() => {
      // Try scrollIntoView on the top ref (most reliable across nested scroll containers)
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
      // Also try window scroll as fallback
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      // And document scroll for good measure
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    return () => clearTimeout(timer);
  }, [step]);
  
  // Check feature flags
  const features = currentUser?.company?.features || [];
  const prepopulatedCashFlowOnly = features.includes('prepopulated_cash_flow_only');

  // Auto-select pre-populated if feature flag is enabled
  if (prepopulatedCashFlowOnly && !cfaType) {
    updateCfaType('pre-populated');
    updateStep(1);
  }

  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <SuccessionCfaPrice 
            updateStep={updateStep} 
            step={step} 
            cashFlows={cashFlows} 
            updateCashFlows={updateCashFlows} 
            currentUser={currentUser}
          />
        );
      case 2:
        return (
          <SuccessionCfaDisclaimer 
            updateStep={updateStep} 
            step={step} 
            currentUser={currentUser} 
          />
        );
      case 3:
        // Interstitial while generating deal structures
        return (
          <CfaGeneratingInterstitial 
            onComplete={() => updateStep(4)} 
          />
        );
      case 4:
        return (
          <SuccessionCfaViewExamples 
            cashFlows={cashFlows} 
            currentUser={currentUser}
          />
        );
      default:
        return (
          <SuccessionCfaChoice 
            cfaType={cfaType} 
            updateCfaType={updateCfaType} 
            step={step} 
            updateStep={updateStep} 
            currentUser={currentUser}
          />
        );
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[var(--succession-bg-secondary)]">
      <div ref={topRef} />
      <div className="flex-1 p-6 md:p-12">
        {renderStep()}
      </div>
    </div>
  );
};

export default SuccessionCfaStart; 